/**
 * Indicação guardada no navegador de quem abriu um link `/r/<codigo>`.
 *
 * Vale 30 dias a partir do clique — a mesma janela que o servidor confere.
 * O navegador guarda um clique só (o último); quem decide se vira vínculo é
 * o servidor, no pagamento, e lá o PRIMEIRO vínculo do cliente é que vale.
 *
 * Também manda para a API os passos do funil (acesso, peça vista, peça no
 * carrinho) enquanto houver indicação — sem ela, nada é enviado.
 */
import type { TrackEvent, TrackedEventType } from "@montebox/shared/campaigns";

import { API_URL, api } from "@/lib/api";

const KEY = "montebox:ref";
const VISITOR_KEY = "montebox:vid";
const WINDOW_MS = 30 * 86_400_000;

export interface StoredReferral {
  code: string;
  /** Nome de exibição do revendedor. */
  name: string;
  /** Slug da campanha, quando o link era `/r/<codigo>/<campanha>`. */
  campaign?: string;
  clickedAt: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
}

interface PublicInfo {
  code: string;
  name: string;
  campaign: {
    slug: string;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmContent: string | null;
  } | null;
}

const store = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * Id aleatório deste navegador — só para contar acessos únicos.
 *
 * Não identifica ninguém: não sai do navegador junto com nome, e-mail ou
 * WhatsApp, e some se a pessoa limpar os dados do site.
 */
export const visitorId = (): string => {
  const saved = store()?.getItem(VISITOR_KEY);
  if (saved) return saved;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  store()?.setItem(VISITOR_KEY, id);
  return id;
};

/**
 * Confere o código (e a campanha) na API e guarda a indicação.
 *
 * Código inválido não guarda nada. Campanha que não existe não invalida o
 * link: a indicação vale, só sem campanha. As UTMs de uma campanha
 * cadastrada valem sobre as da URL.
 */
export const registerReferral = async (
  code: string,
  campaign: string | undefined,
  utm: Record<string, string | undefined>,
): Promise<StoredReferral | null> => {
  const info = await lookup(code, campaign);
  if (!info) return null;
  const fromCampaign = info.campaign
    ? {
        campaign: info.campaign.slug,
        utmSource: info.campaign.utmSource,
        utmMedium: info.campaign.utmMedium,
        utmCampaign: info.campaign.utmCampaign,
        ...(info.campaign.utmContent ? { utmContent: info.campaign.utmContent } : {}),
      }
    : Object.fromEntries(Object.entries(utm).filter(([, value]) => value));
  const referral: StoredReferral = {
    code: info.code,
    name: info.name,
    clickedAt: new Date().toISOString(),
    ...fromCampaign,
  };
  store()?.setItem(KEY, JSON.stringify(referral));
  track("ACESSO");
  return referral;
};

const lookup = async (code: string, campaign: string | undefined): Promise<PublicInfo | null> => {
  const base = `/referral/${encodeURIComponent(code)}`;
  try {
    return await api.get<PublicInfo>(campaign ? `${base}/${encodeURIComponent(campaign)}` : base, {
      auth: false,
    });
  } catch {
    if (!campaign) return null;
    // Campanha errada no link: vale como link principal.
    return lookup(code, undefined);
  }
};

/** Indicação ainda dentro da janela, ou nada. */
export const currentReferral = (): StoredReferral | null => {
  const raw = store()?.getItem(KEY);
  if (!raw) return null;
  try {
    const referral = JSON.parse(raw) as StoredReferral;
    if (Date.now() - new Date(referral.clickedAt).getTime() > WINDOW_MS) {
      store()?.removeItem(KEY);
      return null;
    }
    return referral;
  } catch {
    store()?.removeItem(KEY);
    return null;
  }
};

/** O que vai junto com o pedido — sem o nome, que é só de exibição. */
export const referralPayload = (referral: StoredReferral | null) => {
  if (!referral) return undefined;
  const { name: _name, ...payload } = referral;
  return { ...payload, visitorId: visitorId() };
};

// --------------------------------- Funil -------------------------------------

let queue: TrackEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * Registra um passo do funil. Sem indicação ativa, não faz nada.
 *
 * Os eventos vão em lote (um envio a cada poucos segundos, ou ao sair da
 * página) — quem monta um kit de 6 peças não dispara 12 requisições. Falha
 * de rede é engolida: métrica nunca atrapalha a compra.
 */
export const track = (
  type: TrackedEventType,
  detail: { pieceId?: string; quantity?: number } = {},
): void => {
  if (!currentReferral()) return;
  queue.push({ type, at: new Date().toISOString(), ...detail });
  if (queue.length >= 20) {
    flush();
    return;
  }
  timer ??= setTimeout(flush, 3000);
};

const flush = (): void => {
  if (timer) clearTimeout(timer);
  timer = null;
  const referral = currentReferral();
  if (!referral || queue.length === 0) {
    queue = [];
    return;
  }
  const events = queue.splice(0, 20);
  const body = JSON.stringify({
    visitorId: visitorId(),
    code: referral.code,
    ...(referral.campaign ? { campaign: referral.campaign } : {}),
    ...(referral.utmSource ? { utmSource: referral.utmSource } : {}),
    ...(referral.utmMedium ? { utmMedium: referral.utmMedium } : {}),
    ...(referral.utmCampaign ? { utmCampaign: referral.utmCampaign } : {}),
    ...(referral.utmContent ? { utmContent: referral.utmContent } : {}),
    events,
  });
  // keepalive: o lote sai mesmo se a pessoa fechar a aba logo depois.
  void fetch(`${API_URL}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
  if (queue.length > 0) timer = setTimeout(flush, 3000);
};

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

// ------------------------------ Link na URL ----------------------------------

export interface UtmSearch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

/** UTMs da query string, para o `validateSearch` das rotas de link. */
export const utmSearch = (search: Record<string, unknown>): UtmSearch => {
  const out: UtmSearch = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content"] as const) {
    const value = text(search[key]);
    if (value) out[key] = value;
  }
  return out;
};
